import { ccfRoutes } from '@app/ccf/ccf-routes';
import { useGenerateSupplyChainFlowTemplate } from '@shared/api/hooks/ccf/activities/supply-chain-logistics';
import {
  TypeC5bEnum,
  TypeOfSuppliersEnum,
  WeightOptionEnum,
} from '@shared/api/types';
import Text from '@shared/components/content/text';
import GooglePlacePicker from '@shared/components/form/google-place-picker';
import SelectFormField from '@shared/components/form/select-form-field';
import GuideTemplate from '@shared/components/guide-template';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { useFormContext } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';
import { useCCF } from '../../ccf-context';
import {
  supplierProximityOptions,
  typeOfSuppliersOptions,
} from '../supply-options';
import { reset } from 'mixpanel-browser';

export default function SupplyFlowAboutSuppliersPage() {
  const { trigger, getValues, reset, watch } = useFormContext();
  const { uuid: reportUuid } = useCCF();
  const navigate = useNavigate();
  const { mutateAsync: generateTemplate, isLoading: isGeneratingTemplate } =
    useGenerateSupplyChainFlowTemplate();
  const { toast } = useToast();

  const typeOfSuppliers = watch('typeOfSuppliers');

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const typeOfSuppliers = getValues('typeOfSuppliers');
    const originLocation = getValues('originLocation');

    if (
      typeOfSuppliers === 'international' &&
      (!originLocation || originLocation === '')
    ) {
      toast({
        title: 'Error',
        description: 'Origin location is required for international suppliers.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const isValid = await trigger([
        'typeOfSuppliers',
        'supplierProximity',
        'originLocation',
      ]);

      const suppliersProximity = getValues('suppliersProximity');

      if (isValid) {
        const res = await generateTemplate({
          data: {
            typeOfSuppliers,
            type: TypeC5bEnum['supply'],
            originLocation:
              typeOfSuppliers === TypeOfSuppliersEnum['international_supplier']
                ? originLocation
                : undefined,
            suppliersProximity:
              typeOfSuppliers === TypeOfSuppliersEnum['international_supplier']
                ? 'national'
                : suppliersProximity,
          },
        });

        reset({
          ...res,
          weightOption: WeightOptionEnum['purchased_goods'],
          originLocation: res.originLocation,
          segments: res.segments.map((segment) => ({
            nodeName: segment.nodeName,
            nodeType: segment.nodeType,
            storageManagedBy: segment.storageManagedBy,
            transportMode: segment.transportMode,
            transportManagedBy: segment.transportManagedBy,
            storageDuration: segment.storageDuration,
            distance: String(segment.distance),
          })),
        });
        navigate(
          generatePath(ccfRoutes.SUPPLY_FLOW_REVIEW, {
            report_uuid: reportUuid,
            flow_uuid: '',
          })
        );
      }
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Error',
        description: 'There was an error generating the supply flow template.',
        variant: 'destructive',
      });
    }
  };

  return (
    <GuideTemplate
      preTitle="ADD SUPPLY FLOW"
      title="About your suppliers"
      description={
        <Text>
          Start by telling us about who you are purchasing from, if you purchase
          from multiple types of supplier, you will likely need to create a
          separate flow to model the distribution from each supplier separately.
        </Text>
      }
      primaryActionText="Generate flow"
      primaryActionProps={{
        form: 'about-suppliers-form',
        type: 'submit',
        loading: isGeneratingTemplate,
      }}
    >
      <form id="about-suppliers-form" onSubmit={onSubmit}>
        <div className="flex w-full flex-col gap-6">
          <SelectFormField
            name="typeOfSuppliers"
            label="Who do you buy your goods from?"
            options={typeOfSuppliersOptions}
            placeholder="Select a type of supplier"
            className="w-72"
            disabled={isGeneratingTemplate}
          />

          {typeOfSuppliers !== TypeOfSuppliersEnum['international_supplier'] ? (
            <SelectFormField
              name="suppliersProximity"
              label="How close are they to your sites?"
              options={supplierProximityOptions}
              placeholder="Select a proximity"
              className="w-72"
              disabled={isGeneratingTemplate}
            />
          ) : (
            <GooglePlacePicker
              name="originLocation"
              className="w-72"
              label="Where do they ship to you from?"
              includeErrorMessage={true}
              disabled={isGeneratingTemplate}
            />
          )}
        </div>
      </form>
    </GuideTemplate>
  );
}
